import { getCookieConsent } from '../contexts/CookieConsentContext';

const LINKEDIN_PARTNER_ID = '10419601';

/**
 * LinkedIn conversion IDs. Each maps to a Campaign Manager conversion goal.
 * `PRICING_PAGE_VIEW` is the page-view conversion for the DEL-241 LinkedIn ad test.
 * The numeric IDs are placeholders until Syd wires the live IDs from LinkedIn
 * Campaign Manager; the *call shape* is what DEL-254 needs to lock in.
 */
export const LINKEDIN_CONVERSIONS = Object.freeze({
  PRICING_PAGE_VIEW: '10419601',
});

let isLinkedInInitialized = false;
let lastSentIdentity = null;

/**
 * SHA-256 hash a string to lowercase hex. LinkedIn's advanced matching
 * requires this exact normalization (lowercase + trim + SHA-256) for the
 * `email` field. Returns lowercase hex string.
 *
 * Returns `null` if `crypto.subtle` is unavailable (older browsers, SSR,
 * or non-secure context). The caller is expected to fall back to no-email
 * tracking when null is returned.
 */
export const sha256Hex = async (text) => {
  if (typeof text !== 'string' || typeof window === 'undefined') return null;
  if (!window.crypto || !window.crypto.subtle || !window.crypto.subtle.digest) {
    return null;
  }
  const normalized = text.trim().toLowerCase();
  const buf = new TextEncoder().encode(normalized);
  const digest = await window.crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
};

/**
 * Inject the LinkedIn Insight Tag.
 *
 * Gated behind the `marketing` cookie-consent category, matching the
 * site's existing pattern for GA4 and PostHog. The script is registered
 * for every page via AnalyticsTracker; it only fires once the user has
 * accepted marketing cookies.
 *
 * The <noscript> fallback pixel from LinkedIn's standard snippet is
 * intentionally omitted: it cannot be consent-gated (noscript only
 * renders when JS is disabled, which is precisely when the consent
 * banner can't run), and firing a tracking pixel for users who can't
 * consent is a PECR issue.
 */
export const initLinkedInInsight = () => {
  if (typeof window === 'undefined' || isLinkedInInitialized) {
    return;
  }

  const consent = getCookieConsent('marketing');
  if (!consent) {
    if (process.env.NODE_ENV === 'development') {
      console.log('LinkedIn Insight: Skipping initialization due to lack of marketing consent');
    }
    return;
  }

  // _linkedin_data_partner_ids must be set before the loader script runs.
  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);

  // Mark initialized BEFORE injecting the loader so a blocked / failed
  // script (e.g. uBlock / Privacy Badger / Brave shielding snap.licdn.com)
  // does not cause the per-second consent poll to keep retrying the IIFE.
  // Once isLinkedInInitialized is true, initLinkedInInsight early-returns
  // and we don't accumulate dangling script tags or thrown errors.
  isLinkedInInitialized = true;

  try {
    // Loader script (matches the official LinkedIn Insight Tag IIFE).
    (function (l) {
      if (!l) {
        window.lintrk = function (a, b) { window.lintrk.q.push([a, b]); };
        window.lintrk.q = [];
      }
      var s = document.getElementsByTagName('script')[0];
      var b = document.createElement('script');
      b.type = 'text/javascript';
      b.async = true;
      b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
      s.parentNode.insertBefore(b, s);
    })(window.lintrk);
  } catch (err) {
    // ponytail: if a privacy extension nukes the loader (no <script> tags,
    // detached head, etc.), swallow it. Future init attempts will no-op
    // because isLinkedInInitialized is already true.
    if (process.env.NODE_ENV === 'development') {
      console.warn('LinkedIn Insight: loader IIFE failed (likely blocked)', err);
    }
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('LinkedIn Insight initialized');
  }
};

/**
 * Pass the logged-in user's identifier to the LinkedIn Insight Tag for
 * advanced matching. LinkedIn uses this to attribute conversions to the
 * correct LinkedIn member when the user lands via a LinkedIn ad.
 *
 * Required by DEL-254 spec:
 *   - `uid` (user ID if logged in) - passed as `user_id`
 *   - `email` (hashed, if logged in) - SHA-256 hash of lowercased+trimmed email
 *
 * Gated behind `marketing` consent. Idempotent - repeated calls with the
 * same identity do not duplicate. Safe to call on every page navigation.
 *
 * Returns a Promise resolving to a boolean indicating whether the call
 * was actually made (vs. no-op'd because of gating).
 */
export const setLinkedInUserData = async (userId, email) => {
  if (typeof window === 'undefined') return false;
  if (!isLinkedInInitialized) return false;

  const consent = getCookieConsent('marketing');
  if (!consent) return false;

  if (!userId && !email) return false;

  if (typeof window.lintrk !== 'function') return false;

  const payload = {};
  if (userId) {
    payload.user_id = String(userId);
  }
  if (email) {
    const hashed = await sha256Hex(email);
    if (hashed) {
      payload.email = hashed;
    }
  }

  if (Object.keys(payload).length === 0) return false;

  // Skip if this exact identity has already been sent. AnalyticsTracker
  // calls us on a 1-second consent poll, and we don't want to spam
  // lintrk('track', ...) once per second for the lifetime of the page.
  const identity = JSON.stringify(payload);
  if (lastSentIdentity === identity) return false;
  lastSentIdentity = identity;

  try {
    window.lintrk('track', payload);
    return true;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('LinkedIn Insight: lintrk(track) failed for user data', err);
    }
    return false;
  }
};

/**
 * Fire a LinkedIn conversion event. Each conversion maps to a Campaign
 * Manager goal configured by Syd. DEL-254 wires the pricing page view as
 * the primary conversion for the LinkedIn ad test launched under DEL-241.
 *
 * Gated behind `marketing` consent. Safe to call on every page view;
 * LinkedIn's tag dedupes by conversion_id.
 *
 * Returns a boolean indicating whether the call was actually made.
 */
export const trackLinkedInConversion = (conversionId) => {
  if (typeof window === 'undefined') return false;
  if (!isLinkedInInitialized) return false;

  const consent = getCookieConsent('marketing');
  if (!consent) return false;

  if (!conversionId) return false;

  if (typeof window.lintrk !== 'function') return false;

  try {
    window.lintrk('track', { conversion_id: conversionId });
    return true;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('LinkedIn Insight: lintrk(track) failed for conversion', err);
    }
    return false;
  }
};

/**
 * Test-only reset hook. Not part of the public analytics API.
 * Restores the module to a fresh state so the next test can re-init.
 */
export const __resetLinkedInState = () => {
  isLinkedInInitialized = false;
  lastSentIdentity = null;
};
